import React, { useState, useEffect, useMemo } from 'react';
import { WorkflowManagement } from '@/components/WorkflowManagement';
import type { Employee } from '@/types/timesheet';
import { toast } from 'sonner';
import { getAllEmployees } from '@/services/employeeService';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { initializeMockData } from '@/data/mockData';

export const ApprovalWorkflowSettings: React.FC = () => {
  const { isDemo, isLive } = useScreenDataMode('settings-approval');
  
  const [liveEmployees, setLiveEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get demo employees from mock data
  const demoEmployees = useMemo(() => {
    return initializeMockData().employees;
  }, []);

  // Fetch live data when in live mode
  useEffect(() => {
    if (isLive) {
      fetchLiveEmployees();
    }
  }, [isLive]);

  const fetchLiveEmployees = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAllEmployees();
      setLiveEmployees(data);
    } catch (err) {
      const errorMessage = 'Failed to load employees from API';
      setError(errorMessage);
      console.error('Error fetching live employees:', err);
      toast.error('Failed to load employees', {
        description: 'Could not connect to the API. Please ensure the backend is running or switch to demo mode.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Use live data when available, otherwise use demo data
  const employees = useMemo(() => {
    if (isLive && liveEmployees.length > 0) {
      return liveEmployees;
    }
    if (isDemo) {
      return demoEmployees;
    }
    return liveEmployees;
  }, [isLive, isDemo, liveEmployees, demoEmployees]);

  // Show loading state only in live mode
  if (isLive && isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading employees...</div>
      </div>
    );
  }

  // Show error state only in live mode
  if (isLive && error && !isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-3">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchLiveEmployees}
            className="text-sm text-blue-600 hover:underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return <WorkflowManagement employees={employees} isDemo={isDemo} isLive={isLive} />;
};
